import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus, ChevronDown, ChevronUp, Edit2, Trash2, FileText, Loader2, X, Check, Upload, Paperclip, ExternalLink } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { normasService, NormaInterna, formatDocumentSize } from '../../../services/normasService';
import { canCreateNorma, createNormaWithAttachment } from './normaComposer';
import { useKeycloak } from '../../../contexts/KeycloakContext';
import toast from 'react-hot-toast';

interface NormasPanelProps {
  customerId: string;
  onBack?: () => void;
  lightTheme?: boolean;
}

interface NormaFormState {
  title: string;
  content: string;
}

const NormasPanel: React.FC<NormasPanelProps> = ({ customerId, onBack, lightTheme = false }) => {
  const { hasAnyRole } = useKeycloak();
  const isAdmin = hasAnyRole('admin', 'supervisor', 'sindico', 'super_admin');

  const [normas, setNormas] = useState<NormaInterna[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Create form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<NormaFormState>({ title: '', content: '' });
  const [creating, setCreating] = useState(false);
  // The attachment picked while composing — a PDF or a photo. "Nova norma" used to take a title
  // and a text only, so publishing the regimento meant creating an empty norma and then attaching
  // to it from the list, which nothing on the screen told you about.
  const [createFile, setCreateFile] = useState<File | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<NormaFormState>({ title: '', content: '' });
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // PDF attachment — per-norma, so two uploads can't share one spinner.
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [removingDocId, setRemovingDocId] = useState<string | null>(null);

  const handlePickDocument = async (normaId: string, file: File | undefined) => {
    if (!file) return;
    // Checked here as well as server-side: a 10MB upload that is going to be rejected should not
    // be sent over a slow connection first.
    if (file.size > 10 * 1024 * 1024) {
      toast.error('O PDF excede o limite de 10MB');
      return;
    }
    if (file.type && file.type !== 'application/pdf') {
      toast.error('Apenas arquivos PDF são permitidos');
      return;
    }
    setUploadingId(normaId);
    try {
      await normasService.uploadDocument(customerId, normaId, file);
      toast.success('PDF anexado com sucesso');
      // Reload rather than patching locally: the URL the server returns is presigned and
      // short-lived, and the list is the thing that keeps it fresh.
      loadNormas();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao anexar o PDF');
    } finally {
      setUploadingId(null);
    }
  };

  const handleRemoveDocument = async (normaId: string) => {
    if (!window.confirm('Remover o PDF anexado desta norma?')) return;
    setRemovingDocId(normaId);
    try {
      await normasService.deleteDocument(customerId, normaId);
      toast.success('PDF removido');
      loadNormas();
    } catch {
      toast.error('Erro ao remover o PDF');
    } finally {
      setRemovingDocId(null);
    }
  };

  const loadNormas = useCallback(async () => {
    setLoading(true);
    try {
      const data = await normasService.listNormas(customerId);
      setNormas(data);
    } catch {
      toast.error('Erro ao carregar normas internas');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadNormas();
  }, [loadNormas]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    const draft = { title: createForm.title, content: createForm.content, order: normas.length, file: createFile };
    if (!canCreateNorma(draft)) {
      toast.error('Título e conteúdo são obrigatórios');
      return;
    }
    setCreating(true);
    // Create, then attach: a norma's document uploads to its own id, so there is nothing to
    // attach to until it exists. The second step failing is NOT a failed creation — see
    // createNormaWithAttachment.
    const outcome = await createNormaWithAttachment(
      draft,
      payload => normasService.createNorma(customerId, payload),
      (normaId, file) => normasService.uploadDocument(customerId, normaId, file),
    );
    setCreating(false);

    if (outcome.kind === 'failed') {
      toast.error('Erro ao criar norma');
      return;
    }
    if (outcome.kind === 'created-without-attachment') {
      toast.error('Norma criada, mas o anexo não foi enviado. Use "Anexar" na norma.');
    } else {
      toast.success('Norma criada com sucesso');
    }
    setCreateForm({ title: '', content: '' });
    setCreateFile(null);
    setShowCreateForm(false);
    loadNormas();
  };

  const handleStartEdit = (norma: NormaInterna) => {
    setEditingId(norma.id);
    setEditForm({ title: norma.title, content: norma.content });
    setExpandedIds(prev => new Set(Array.from(prev).concat(norma.id)));
  };

  const handleSaveEdit = async (normaId: string) => {
    if (!editForm.title.trim() || !editForm.content.trim()) {
      toast.error('Título e conteúdo são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      await normasService.updateNorma(customerId, normaId, {
        title: editForm.title.trim(),
        content: editForm.content.trim(),
      });
      toast.success('Norma atualizada');
      setEditingId(null);
      loadNormas();
    } catch {
      toast.error('Erro ao atualizar norma');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (normaId: string) => {
    setDeletingId(normaId);
    try {
      await normasService.deleteNorma(customerId, normaId);
      toast.success('Norma removida');
      loadNormas();
    } catch {
      toast.error('Erro ao remover norma');
    } finally {
      setDeletingId(null);
    }
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const bg = lightTheme ? 'bg-white' : 'bg-gray-950';
  const border = lightTheme ? 'border-gray-200' : 'border-gray-800';
  const text = lightTheme ? 'text-gray-900' : 'text-gray-100';
  const subtext = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const cardBg = lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-900 border-gray-700';
  const inputClass = cn(
    'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
    lightTheme
      ? 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
      : 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500'
  );

  return (
    <div className={cn('flex flex-col h-full', bg)}>
      {/* Header */}
      <div className={cn('flex items-center gap-3 px-4 py-3 border-b shrink-0', border)}>
        {onBack && (
          <button
            onClick={onBack}
            className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-400')}
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <h2 className={cn('font-semibold text-base uppercase tracking-wide', text)}>Normas Internas</h2>
        <div className="flex-1" />
        {isAdmin && (
          <button
            onClick={() => { setShowCreateForm(true); setEditingId(null); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
          >
            <Plus size={14} />
            Nova Norma
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3">
        {/* Create form */}
        {showCreateForm && isAdmin && (
          <div className={cn('rounded-xl border p-4 space-y-3', cardBg)}>
            <h3 className={cn('font-medium text-sm', text)}>Nova Norma</h3>
            <input
              className={inputClass}
              placeholder="Título da norma"
              value={createForm.title}
              onChange={e => setCreateForm(p => ({ ...p, title: e.target.value }))}
              maxLength={200}
            />
            <textarea
              className={cn(inputClass, 'h-32 resize-none')}
              placeholder="Conteúdo da norma..."
              value={createForm.content}
              onChange={e => setCreateForm(p => ({ ...p, content: e.target.value }))}
            />
            {/* PDF or photo, attached while composing — the same thing the Mural's composer does.
                Uploaded after the norma is created, because the endpoint is per-norma. */}
            <label
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors w-fit',
                lightTheme ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-gray-200',
              )}
            >
              <Paperclip size={14} />
              {createFile ? createFile.name : 'Anexar PDF ou foto (opcional)'}
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => {
                  setCreateFile(e.target.files?.[0] ?? null);
                  // Cleared so re-picking the SAME file fires onChange again.
                  e.target.value = '';
                }}
              />
            </label>
            {createFile && (
              <button
                onClick={() => setCreateFile(null)}
                className={cn('text-xs font-medium', lightTheme ? 'text-red-600 hover:text-red-800' : 'text-red-400 hover:text-red-300')}
              >
                Remover anexo
              </button>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowCreateForm(false); setCreateForm({ title: '', content: '' }); setCreateFile(null); }}
                className={cn('px-3 py-1.5 text-sm rounded-lg transition-colors', lightTheme ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-gray-300')}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
              >
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Salvar
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className={cn('animate-spin', subtext)} />
          </div>
        ) : normas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 space-y-3">
            <FileText size={40} className={subtext} />
            <p className={cn('text-sm', subtext)}>Nenhuma norma interna cadastrada</p>
            {isAdmin && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="text-sm text-blue-500 hover:text-blue-400 transition-colors"
              >
                Adicionar primeira norma
              </button>
            )}
          </div>
        ) : (
          normas.map((norma, idx) => {
            const isExpanded = expandedIds.has(norma.id);
            const isEditing = editingId === norma.id;
            const isDeleting = deletingId === norma.id;

            return (
              <div key={norma.id} className={cn('rounded-xl border overflow-hidden', cardBg)}>
                {/* Card header */}
                <button
                  className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:opacity-80"
                  onClick={() => { if (!isEditing) toggleExpand(norma.id); }}
                >
                  <span className={cn('text-xs font-mono w-6 shrink-0 text-center', subtext)}>
                    {idx + 1}.
                  </span>
                  <span className={cn('flex-1 font-medium text-sm', text)}>{norma.title}</span>
                  {norma.document_url && (
                    // Visible while collapsed, so "which normas have the PDF?" doesn't require
                    // expanding every one of them. The tooltip lives on the wrapper: lucide icons
                    // don't accept `title`.
                    <span className="shrink-0" title={norma.document_name || 'PDF anexado'}>
                      <Paperclip size={13} className={subtext} />
                    </span>
                  )}
                  <div className="flex items-center gap-1 shrink-0">
                    {isAdmin && !isEditing && (
                      <>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={e => { e.stopPropagation(); handleStartEdit(norma); }}
                          onKeyDown={e => e.key === 'Enter' && handleStartEdit(norma)}
                          className={cn('p-1.5 rounded-lg transition-colors cursor-pointer', lightTheme ? 'hover:bg-gray-200 text-gray-500' : 'hover:bg-gray-700 text-gray-400')}
                        >
                          <Edit2 size={13} />
                        </span>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={e => { e.stopPropagation(); handleDelete(norma.id); }}
                          onKeyDown={e => e.key === 'Enter' && handleDelete(norma.id)}
                          className={cn('p-1.5 rounded-lg transition-colors cursor-pointer', lightTheme ? 'hover:bg-red-50 text-gray-400 hover:text-red-600' : 'hover:bg-red-900/30 text-gray-500 hover:text-red-400')}
                        >
                          {isDeleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        </span>
                      </>
                    )}
                    {isEditing ? (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={e => { e.stopPropagation(); setEditingId(null); }}
                        onKeyDown={e => e.key === 'Enter' && setEditingId(null)}
                        className={cn('p-1.5 rounded-lg transition-colors cursor-pointer', lightTheme ? 'hover:bg-gray-200 text-gray-500' : 'hover:bg-gray-700 text-gray-400')}
                      >
                        <X size={13} />
                      </span>
                    ) : (
                      isExpanded ? <ChevronUp size={15} className={subtext} /> : <ChevronDown size={15} className={subtext} />
                    )}
                  </div>
                </button>

                {/* Expanded content / edit form */}
                {isExpanded && (
                  <div className={cn('px-4 pb-4 border-t', lightTheme ? 'border-gray-200' : 'border-gray-700')}>
                    {isEditing ? (
                      <div className="pt-3 space-y-3">
                        <input
                          className={inputClass}
                          value={editForm.title}
                          onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                          maxLength={200}
                        />
                        <textarea
                          className={cn(inputClass, 'h-32 resize-none')}
                          value={editForm.content}
                          onChange={e => setEditForm(p => ({ ...p, content: e.target.value }))}
                        />
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => setEditingId(null)}
                            className={cn('px-3 py-1.5 text-sm rounded-lg transition-colors', lightTheme ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-gray-300')}
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={() => handleSaveEdit(norma.id)}
                            disabled={saving}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
                          >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            Salvar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="pt-3 max-h-60 overflow-y-auto">
                          <p className={cn('text-sm whitespace-pre-wrap leading-relaxed', subtext)}>
                            {norma.content}
                          </p>
                        </div>

                        {/* Attached PDF. The link is visible to everyone who can see the norma —
                            residents need to read the regimento — while attaching and removing are
                            admin-only, mirroring the route guards. */}
                        <div className={cn('mt-3 pt-3 border-t', lightTheme ? 'border-gray-200' : 'border-gray-700')}>
                          {norma.document_url ? (
                            <div className="space-y-2">
                              {/* Shown, not linked. A regimento page IS the norma — the Mural
                                  learned the same thing about a one-page notice (see
                                  MuralAnunciosPanel): burying the document behind "abrir anexo"
                                  hides the content people came to read. "Abrir" stays below for a
                                  40-page convenção.
                                  An older row carries a URL and no document_type — everything
                                  attached before images were allowed — so a missing type reads as
                                  PDF rather than rendering a broken picture. */}
                              {norma.document_type?.startsWith('image/') ? (
                                <a href={norma.document_url} target="_blank" rel="noopener noreferrer">
                                  <img
                                    src={norma.document_url}
                                    alt={norma.title}
                                    className={cn('w-full max-h-96 object-contain rounded-lg border',
                                      lightTheme ? 'border-gray-200 bg-gray-50' : 'border-gray-700 bg-gray-900')}
                                  />
                                </a>
                              ) : (
                                <object
                                  data={norma.document_url}
                                  type="application/pdf"
                                  className={cn('w-full h-96 rounded-lg border',
                                    lightTheme ? 'border-gray-200' : 'border-gray-700')}
                                >
                                  {/* Browsers with no inline PDF viewer land here, not on a blank box. */}
                                  <p className={cn('text-sm p-3', subtext)}>
                                    Não foi possível exibir o PDF neste navegador.
                                  </p>
                                </object>
                              )}
                            <div className="flex items-center gap-2">
                              <a
                                href={norma.document_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Abrir ${norma.document_name || 'documento'}`}
                                className={cn(
                                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors min-w-0',
                                  lightTheme ? 'bg-gray-100 hover:bg-gray-200 text-gray-800' : 'bg-gray-700 hover:bg-gray-600 text-gray-100',
                                )}
                              >
                                <Paperclip size={14} className="shrink-0" />
                                <span className="truncate">{norma.document_name || 'Documento'}</span>
                                {norma.document_size ? (
                                  <span className={cn('shrink-0 text-xs', subtext)}>
                                    {formatDocumentSize(norma.document_size)}
                                  </span>
                                ) : null}
                                <ExternalLink size={12} className="shrink-0 opacity-60" />
                              </a>
                              {isAdmin && (
                                <>
                                  <label
                                    className={cn(
                                      'px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors shrink-0',
                                      lightTheme ? 'hover:bg-gray-200 text-gray-600' : 'hover:bg-gray-700 text-gray-300',
                                    )}
                                  >
                                    {uploadingId === norma.id ? 'Enviando…' : 'Substituir'}
                                    <input
                                      type="file"
                                      accept="application/pdf,image/jpeg,image/png,image/webp"
                                      className="hidden"
                                      disabled={uploadingId === norma.id}
                                      onChange={e => {
                                        handlePickDocument(norma.id, e.target.files?.[0]);
                                        // Cleared so re-picking the SAME file fires onChange again
                                        // (the value is unchanged otherwise, and nothing happens).
                                        e.target.value = '';
                                      }}
                                    />
                                  </label>
                                  <button
                                    onClick={() => handleRemoveDocument(norma.id)}
                                    disabled={removingDocId === norma.id}
                                    className={cn(
                                      'p-2 rounded-lg transition-colors shrink-0',
                                      lightTheme ? 'hover:bg-red-50 text-gray-400 hover:text-red-600' : 'hover:bg-red-900/30 text-gray-500 hover:text-red-400',
                                    )}
                                    title="Remover anexo"
                                  >
                                    {removingDocId === norma.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                  </button>
                                </>
                              )}
                            </div>
                            </div>
                          ) : isAdmin ? (
                            <label
                              className={cn(
                                'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors',
                                lightTheme ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-gray-200',
                              )}
                            >
                              {uploadingId === norma.id
                                ? <Loader2 size={14} className="animate-spin" />
                                : <Upload size={14} />}
                              {uploadingId === norma.id ? 'Enviando…' : 'Anexar PDF ou foto'}
                              <input
                                type="file"
                                accept="application/pdf,image/jpeg,image/png,image/webp"
                                className="hidden"
                                disabled={uploadingId === norma.id}
                                onChange={e => {
                                  handlePickDocument(norma.id, e.target.files?.[0]);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          ) : null}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default NormasPanel;
